import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Form,
  Progress,
  Radio,
  Row,
  Select,
  Space,
  Statistic,
  TreeSelect,
  Typography,
} from 'antd';
import { SwapOutlined, SyncOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ApiError } from '@mall/shared';
import { useToast } from '../../app/ToastProvider';
import { listCategories } from '../categories/api';
import { categoryDisplayName, type CategoryView } from '../categories/types';
import { listTenants } from '../tenants/api';
import type { TenantView } from '../tenants/types';
import { createProductSyncJob, getProductSyncJob, listProducts } from './api';
import type { ProductSyncJobView, ProductSyncMode } from './types';

interface CategoryTreeOption {
  title: string;
  value: number;
  children: CategoryTreeOption[];
}

function buildCategoryOptions(rows: CategoryView[]): CategoryTreeOption[] {
  const nodes = new Map<number, CategoryTreeOption>();
  for (const row of rows) {
    nodes.set(row.id, { title: categoryDisplayName(row), value: row.id, children: [] });
  }
  const roots: CategoryTreeOption[] = [];
  for (const row of rows) {
    const node = nodes.get(row.id);
    if (!node) continue;
    if (row.parentId && nodes.has(row.parentId)) {
      nodes.get(row.parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

const TERMINAL_STATUSES: ProductSyncJobView['status'][] = ['DONE', 'FAILED'];

export function SyncPage() {
  const toast = useToast();
  const [sourceTenantId, setSourceTenantId] = useState<number | undefined>();
  const [targetTenantId, setTargetTenantId] = useState<number | undefined>();
  const [mode, setMode] = useState<ProductSyncMode>('ALL');
  const [categoryId, setCategoryId] = useState<number | undefined>();
  const [productIds, setProductIds] = useState<number[]>([]);
  const [productKeyword, setProductKeyword] = useState('');
  const [jobId, setJobId] = useState<number | null>(null);

  const tenantsQuery = useQuery({ queryKey: ['operator', 'tenants'], queryFn: listTenants });
  const categoriesQuery = useQuery({
    queryKey: ['operator', 'categories'],
    queryFn: listCategories,
  });

  const tenants = tenantsQuery.data ?? [];
  const tenantMap = useMemo(() => new Map(tenants.map((t) => [t.id, t])), [tenants]);
  const categoryOptions = useMemo(
    () => buildCategoryOptions(categoriesQuery.data ?? []),
    [categoriesQuery.data],
  );

  // Source products for the manual-pick (IDS) mode.
  const sourceProductsQuery = useQuery({
    queryKey: ['operator', 'sync-source-products', sourceTenantId, productKeyword],
    queryFn: () =>
      listProducts({
        tenantId: sourceTenantId!,
        page: 1,
        pageSize: 50,
        keyword: productKeyword.trim() || undefined,
      }),
    enabled: mode === 'IDS' && sourceTenantId != null,
  });

  const jobQuery = useQuery({
    queryKey: ['operator', 'sync-job', jobId],
    queryFn: () => getProductSyncJob(jobId!),
    enabled: jobId != null,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (!status || TERMINAL_STATUSES.includes(status)) {
        return false;
      }
      return 1500;
    },
  });

  const job = jobQuery.data;
  const running = job != null && !TERMINAL_STATUSES.includes(job.status);

  const createMutation = useMutation({
    mutationFn: () =>
      createProductSyncJob({
        sourceTenantId: sourceTenantId!,
        targetTenantId: targetTenantId!,
        mode,
        categoryId: mode === 'CATEGORY' ? categoryId : undefined,
        productIds: mode === 'IDS' ? productIds : undefined,
      }),
    onSuccess: (created) => {
      toast.success('同步任务已启动');
      setJobId(created.id);
    },
    onError: (err: Error) => {
      toast.error(err instanceof ApiError ? err.message : '启动同步任务失败');
    },
  });

  const canSubmit =
    sourceTenantId != null &&
    targetTenantId != null &&
    sourceTenantId !== targetTenantId &&
    (mode !== 'CATEGORY' || categoryId != null) &&
    (mode !== 'IDS' || productIds.length > 0) &&
    !running;

  const swapTenants = () => {
    setSourceTenantId(targetTenantId);
    setTargetTenantId(sourceTenantId);
    setProductIds([]);
  };

  const progressPercent =
    job && job.total > 0 ? Math.round((job.processed / job.total) * 100) : running ? 0 : 0;

  return (
    <div className="mall-page">
      <div className="mall-page-header">
        <div>
          <div className="mall-page-kicker">Sync</div>
          <Typography.Title level={3} className="mall-page-title">
            商品同步
          </Typography.Title>
          <p className="mall-page-description">
            从来源租户复制商品到目标租户：已存在的按来源更新，不存在的新增。被锁定的商品会跳过，目标租户的售价 / 上下架 / 库存保持不变。
          </p>
        </div>
      </div>

      <div className="mall-filter-card">
        <Form layout="vertical">
          <Row gutter={12} align="bottom">
            <Col xs={24} md={10} lg={8}>
              <Form.Item label="来源租户" required>
                <Select
                  showSearch
                  placeholder="选择来源租户"
                  loading={tenantsQuery.isLoading}
                  value={sourceTenantId}
                  onChange={(v) => {
                    setSourceTenantId(v);
                    setProductIds([]);
                  }}
                  optionFilterProp="label"
                  options={tenants.map((t: TenantView) => ({
                    label: `${t.name} (${t.slug})`,
                    value: t.id,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={4} lg={2} style={{ textAlign: 'center' }}>
              <Form.Item label=" ">
                <Button icon={<SwapOutlined />} onClick={swapTenants} title="交换来源/目标" />
              </Form.Item>
            </Col>
            <Col xs={24} md={10} lg={8}>
              <Form.Item
                label="目标租户"
                required
                validateStatus={
                  targetTenantId != null && targetTenantId === sourceTenantId ? 'error' : undefined
                }
                help={
                  targetTenantId != null && targetTenantId === sourceTenantId
                    ? '目标租户不能与来源相同'
                    : undefined
                }
              >
                <Select
                  showSearch
                  placeholder="选择目标租户"
                  loading={tenantsQuery.isLoading}
                  value={targetTenantId}
                  onChange={setTargetTenantId}
                  optionFilterProp="label"
                  options={tenants.map((t: TenantView) => ({
                    label: `${t.name} (${t.slug})`,
                    value: t.id,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24}>
              <Form.Item label="同步范围">
                <Radio.Group
                  value={mode}
                  onChange={(e) => setMode(e.target.value as ProductSyncMode)}
                  optionType="button"
                  buttonStyle="solid"
                >
                  <Radio.Button value="ALL">全部商品</Radio.Button>
                  <Radio.Button value="CATEGORY">按分类</Radio.Button>
                  <Radio.Button value="IDS">手动选择</Radio.Button>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>

          {mode === 'CATEGORY' ? (
            <Row gutter={12}>
              <Col xs={24} md={12} lg={8}>
                <Form.Item label="分类（含子分类）" required>
                  <TreeSelect
                    allowClear
                    showSearch
                    treeData={categoryOptions}
                    value={categoryId}
                    onChange={setCategoryId}
                    placeholder="选择分类"
                    loading={categoriesQuery.isLoading}
                    treeNodeFilterProp="title"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>
          ) : null}

          {mode === 'IDS' ? (
            <Row gutter={12}>
              <Col xs={24} md={16} lg={12}>
                <Form.Item label="选择来源商品" required>
                  <Select
                    mode="multiple"
                    showSearch
                    placeholder={sourceTenantId ? '搜索名称 / 编码后选择' : '请先选择来源租户'}
                    disabled={sourceTenantId == null}
                    value={productIds}
                    onChange={setProductIds}
                    onSearch={setProductKeyword}
                    filterOption={false}
                    loading={sourceProductsQuery.isLoading}
                    options={(sourceProductsQuery.data?.list ?? []).map((p) => ({
                      label: `${p.name}（${p.productCode}）`,
                      value: p.id,
                    }))}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>
          ) : null}

          <Button
            type="primary"
            icon={<SyncOutlined />}
            disabled={!canSubmit}
            loading={createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            开始同步
          </Button>
        </Form>
      </div>

      {jobId != null && job ? (
        <div className="mall-table-card" style={{ padding: 20 }}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space wrap>
              <Typography.Text strong>
                {tenantMap.get(job.sourceTenantId)?.name ?? `#${job.sourceTenantId}`} →{' '}
                {tenantMap.get(job.targetTenantId)?.name ?? `#${job.targetTenantId}`}
              </Typography.Text>
              <StatusTag status={job.status} />
            </Space>

            <Progress
              percent={progressPercent}
              status={
                job.status === 'FAILED'
                  ? 'exception'
                  : job.status === 'DONE'
                    ? 'success'
                    : 'active'
              }
            />

            <Row gutter={16}>
              <Col xs={12} md={4}>
                <Statistic title="总计" value={job.total} />
              </Col>
              <Col xs={12} md={4}>
                <Statistic title="已处理" value={job.processed} />
              </Col>
              <Col xs={12} md={4}>
                <Statistic title="新增" value={job.inserted} valueStyle={{ color: '#16a34a' }} />
              </Col>
              <Col xs={12} md={4}>
                <Statistic title="更新" value={job.updated} valueStyle={{ color: '#2563eb' }} />
              </Col>
              <Col xs={12} md={4}>
                <Statistic title="跳过" value={job.skipped} valueStyle={{ color: '#d97706' }} />
              </Col>
              <Col xs={12} md={4}>
                <Statistic title="失败" value={job.failed} valueStyle={{ color: '#dc2626' }} />
              </Col>
            </Row>

            {job.status === 'FAILED' && job.error ? (
              <Alert type="error" showIcon message="同步失败" description={job.error} />
            ) : null}
            {job.status === 'DONE' ? (
              <Alert
                type="success"
                showIcon
                message="同步完成"
                description={`新增 ${job.inserted}，更新 ${job.updated}，跳过 ${job.skipped}，失败 ${job.failed}。`}
              />
            ) : null}
          </Space>
        </div>
      ) : null}
    </div>
  );
}

function StatusTag({ status }: { status: ProductSyncJobView['status'] }) {
  const map: Record<ProductSyncJobView['status'], { color: string; label: string }> = {
    PENDING: { color: '#94a3b8', label: '排队中' },
    RUNNING: { color: '#2563eb', label: '同步中' },
    DONE: { color: '#16a34a', label: '已完成' },
    FAILED: { color: '#dc2626', label: '失败' },
  };
  const item = map[status];
  return <Typography.Text style={{ color: item.color }}>{item.label}</Typography.Text>;
}
